import React, { useState, useCallback } from 'react';
import styles from './ProductForm.module.css';
import type { ProductRecord } from '../../hooks/useTraining';

export interface ProductFormData {
  sku: string;
  name: string;
  brand: string;
  category: string;
  subcategory: string;
  price: number;
  vendor: string;
  vendorSku: string;
  leadTimeDays: number;
  inStock: boolean;
  unit: string;
  notes: string;
}

export interface ProductFormProps {
  onSubmit: (data: ProductFormData) => void;
  onCancel: () => void;
  initialData?: Partial<ProductRecord>;
}

const CATEGORY_OPTIONS = [
  'cabinet', 'countertop', 'tile', 'flooring', 'plumbing', 'lighting',
  'hardware', 'appliance', 'trim', 'paint', 'other',
];

export const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
}) => {
  const [sku, setSku] = useState(initialData?.sku || '');
  const [name, setName] = useState(initialData?.name || '');
  const [brand, setBrand] = useState(initialData?.brand || '');
  const [category, setCategory] = useState(initialData?.category || 'cabinet');
  const [subcategory, setSubcategory] = useState(initialData?.subcategory || '');
  const [price, setPrice] = useState(initialData?.price?.toString() || '');
  const [vendor, setVendor] = useState(initialData?.vendor || '');
  const [vendorSku, setVendorSku] = useState(initialData?.vendorSku || '');
  const [leadTimeDays, setLeadTimeDays] = useState(initialData?.leadTimeDays?.toString() || '0');
  const [inStock, setInStock] = useState(initialData?.inStock !== false);
  const [unit, setUnit] = useState(initialData?.unit || 'each');
  const [notes, setNotes] = useState(initialData?.notes || '');

  const isValid = name.trim().length > 0;
  const isEditMode = !!initialData;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValid) return;
      onSubmit({
        sku: sku.trim(),
        name: name.trim(),
        brand: brand.trim(),
        category,
        subcategory: subcategory.trim(),
        price: parseFloat(price) || 0,
        vendor: vendor.trim(),
        vendorSku: vendorSku.trim(),
        leadTimeDays: parseInt(leadTimeDays, 10) || 0,
        inStock,
        unit: unit.trim() || 'each',
        notes: notes.trim(),
      });
    },
    [isValid, onSubmit, sku, name, brand, category, subcategory, price, vendor, vendorSku, leadTimeDays, inStock, unit, notes]
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Name<span className={styles.required}>*</span>
          </label>
          <input className={styles.input} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name" autoFocus />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>SKU</label>
          <input className={styles.input} type="text" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU" />
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Brand</label>
          <input className={styles.input} type="text" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Brand name" />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Category</label>
          <select className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Subcategory</label>
          <input className={styles.input} type="text" value={subcategory} onChange={(e) => setSubcategory(e.target.value)} placeholder="e.g., wall tile, vanity" />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Unit</label>
          <input className={styles.input} type="text" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="each, sq ft, ln ft" />
        </div>
      </div>

      <div className={styles.fieldRow3}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Price</label>
          <input className={styles.input} type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Lead Time (days)</label>
          <input className={styles.input} type="number" value={leadTimeDays} onChange={(e) => setLeadTimeDays(e.target.value)} placeholder="0" />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>In Stock</label>
          <label className={styles.checkbox}>
            <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} />
            Available
          </label>
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Vendor</label>
          <input className={styles.input} type="text" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Supplier name" />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Vendor SKU</label>
          <input className={styles.input} type="text" value={vendorSku} onChange={(e) => setVendorSku(e.target.value)} placeholder="Vendor part #" />
        </div>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Notes</label>
        <textarea className={styles.textarea} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." rows={2} />
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" className={styles.btnPrimary} disabled={!isValid}>
          {isEditMode ? 'Save Changes' : 'Add Product'}
        </button>
      </div>
    </form>
  );
};
